import { Col, Row } from "antd";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { MdOutlineSchool } from "react-icons/md";
import Button from "shared/components/Button";
import Form from "shared/components/Form";
import Input from "shared/components/Input";
import Label from "shared/components/Label";
import { NAME_SPACES } from "shared/locales/constants";
import { RELATIONSHIP } from "utils/constants";
import { calculateAge } from "utils/helpers/date";

const SchoolCertificate = ({
  values,
  onFileRemove,
  setFieldValue,
  setPercent,
  subscription,
  children: childrenProp,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });

  // Get related persons from subscription
  const relatedPersons = get(
    subscription,
    "project.contact.relatedPersons",
    [],
  ).filter((_) => _);

  // Get children
  const children =
    childrenProp ||
    relatedPersons.filter(
      ({ relationship }) => relationship === RELATIONSHIP.CHILD,
    );

  // Filter children over 20 years old
  const childrenOver20 = children.filter((child) => {
    const birthDate = get(child, "identity.user.birthDate");
    return (
      calculateAge(birthDate, get(values, "project.contract.issuanceDate")) > 20
    );
  });

  // Get the original index of children over 20 in the full children list
  const getOriginalChildIndex = (childOver20) => {
    const userId = get(childOver20, "identity.user.id");
    return children.findIndex((c) => get(c, "identity.user.id") === userId);
  };

  const getChildTitle = (index) => {
    const titles =
      SUBSCRIPTION.FORM.PROOF.CHILDREN_ATTESTATION?.CHILD_TITLES || {};
    switch (index) {
      case 0:
        return titles.FIRST || "Premier enfant";
      case 1:
        return titles.SECOND || "Deuxième enfant";
      case 2:
        return titles.THIRD || "Troisième enfant";
      default:
        return `${index + 1}ème enfant`;
    }
  };

  return (
    <div className="subscription-form--wrapper">
      <Form
        type="vertical"
        title={
          SUBSCRIPTION.FORM.PROOF.STUDENT_ID_OR_ENROLLMENT_CERTIFICATE?.TITLE ||
          "Certificat de scolarité"
        }
        subtitle={
          SUBSCRIPTION.FORM.PROOF.STUDENT_ID_OR_ENROLLMENT_CERTIFICATE
            ?.SUBTITLE ||
          "Obligatoire pour les enfants à charge de plus de 20 ans"
        }
      >
        {childrenOver20.map((child, index) => {
          const userId = get(child, "identity.user.id");
          const firstName = get(child, "identity.user.firstname", "");
          const lastName = get(child, "identity.user.lastname", "");
          const childName = [firstName, lastName].filter(Boolean).join(" ");
          const originalIndex = getOriginalChildIndex(child);
          const title = getChildTitle(originalIndex);
          const titleWithName = childName ? `${title} (${childName})` : title;

          const fieldKey = userId
            ? `childSchoolCertificate_${userId}`
            : `childSchoolCertificate_${index}`;

          return (
            <Row gutter={[16, 16]} key={`school_${userId || index}`}>
              <Col lg={24} xs={24}>
                <div className="subscription-form--group-label">
                  <Label
                    label={titleWithName}
                    icon={<MdOutlineSchool size={16} />}
                    required
                  />
                </div>
              </Col>
              <Col lg={24} xs={24}>
                <div className="subscription-form--custom-upload">
                  <Input
                    type="file"
                    variant="uploader"
                    validTypes={SUBSCRIPTION.FORM.PROOF.ALLOWED_FILES_TYPE}
                    multiple
                    onUpload={(fileList) => {
                      setFieldValue(`proof.${fieldKey}`, fileList);
                      setPercent({
                        ...values,
                        proof: {
                          ...values.proof,
                          [fieldKey]: fileList,
                        },
                      });
                    }}
                    onFileRemove={onFileRemove}
                    fileList={get(values, `proof.${fieldKey}`, [])}
                  >
                    <Row align="center">
                      <Button type="primary--link">
                        {SUBSCRIPTION.FORM.PROOF.ACTION.ADD}
                      </Button>
                    </Row>
                  </Input>
                </div>
              </Col>
            </Row>
          );
        })}
      </Form>
    </div>
  );
};

export default SchoolCertificate;
